import { createFileRoute } from '@tanstack/react-router';
import { AdminMembersView } from '@/components/admin/AdminMembersView';
import type { ListAdminMembersInput } from '@/lib/queries/admin';

type Status = 'active' | 'pending' | 'restricted' | 'deactivated' | 'banned';
type PlatformRoleFilter = 'admin' | 'moderator' | 'none';
type CommunityRoleFilter = 'circle_leader' | 'network_leader';

interface MembersSearch {
  readonly q?: string;
  readonly status?: Status;
  readonly platformRole?: PlatformRoleFilter;
  readonly communityRole?: CommunityRoleFilter;
  readonly cursor?: string;
}

const ALLOWED_STATUS: ReadonlyArray<Status> = [
  'active',
  'pending',
  'restricted',
  'deactivated',
  'banned',
];

const ALLOWED_PLATFORM_ROLE: ReadonlyArray<PlatformRoleFilter> = [
  'admin',
  'moderator',
  'none',
];

const ALLOWED_COMMUNITY_ROLE: ReadonlyArray<CommunityRoleFilter> = [
  'circle_leader',
  'network_leader',
];

export const Route = createFileRoute('/_authed/admin/members/')({
  validateSearch: (raw: Record<string, unknown>): MembersSearch => {
    const q = typeof raw.q === 'string' && raw.q.length > 0 ? raw.q : undefined;
    const status =
      typeof raw.status === 'string' &&
      (ALLOWED_STATUS as ReadonlyArray<string>).includes(raw.status)
        ? (raw.status as Status)
        : undefined;
    const platformRole =
      typeof raw.platformRole === 'string' &&
      (ALLOWED_PLATFORM_ROLE as ReadonlyArray<string>).includes(raw.platformRole)
        ? (raw.platformRole as PlatformRoleFilter)
        : undefined;
    const communityRole =
      typeof raw.communityRole === 'string' &&
      (ALLOWED_COMMUNITY_ROLE as ReadonlyArray<string>).includes(raw.communityRole)
        ? (raw.communityRole as CommunityRoleFilter)
        : undefined;
    const cursor =
      typeof raw.cursor === 'string' && raw.cursor.length > 0
        ? raw.cursor
        : undefined;
    return {
      ...(q !== undefined ? { q } : {}),
      ...(status !== undefined ? { status } : {}),
      ...(platformRole !== undefined ? { platformRole } : {}),
      ...(communityRole !== undefined ? { communityRole } : {}),
      ...(cursor !== undefined ? { cursor } : {}),
    };
  },
  component: AdminMembersPage,
});

function AdminMembersPage() {
  const search = Route.useSearch();
  const listInput: ListAdminMembersInput = {
    pageSize: 50,
    ...(search.q ? { q: search.q } : {}),
    ...(search.status ? { status: search.status } : {}),
    ...(search.platformRole ? { platformRole: search.platformRole } : {}),
    ...(search.communityRole ? { communityRole: search.communityRole } : {}),
    ...(search.cursor ? { cursor: search.cursor } : {}),
  };

  return <AdminMembersView listInput={listInput} cursor={search.cursor} />;
}
